import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Autosuggest from 'react-autosuggest'
import Spinner from 'Components/Spinner'
import SearchResultItem from './components/SearchResultItem'
import AutoCompleteNoResult from './components/AutoCompleteNoResult'
import { _Container, _Divider, _SpinnerContainer } from './styles'


const AutoComplete = ({
  siteSearchIsOpen,
  isMobile,
  resetInputOnSelect,
  result,
  onSuggestionSelect,
  multiSection,
  isFetching,
  placeHolder,
  loadAutoComplete,
  typingStart,
  closeSearch,
  source,
  autoFocus,
}) => {
  const [value, setValue] = useState('')
  const [noSuggestions, setNoSuggestions] = useState(false)
  const [allowCustomValue, setAllowCustomValue] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if(siteSearchIsOpen && !isMobile) {
      inputRef.current.focus()
    }
  }, [siteSearchIsOpen, isMobile])

  useEffect(() => {
    let noSuggestionsDefault = false
    if(result && value !== '') {
      Object.keys(result).forEach((key) => {
        if(!result[key].length) noSuggestionsDefault = true
      })
    }
    if(noSuggestionsDefault !== noSuggestions && !allowCustomValue) {
      setNoSuggestions(noSuggestions)
    }
  }, [result, value, allowCustomValue])

  const onChange = (event, { newValue }) => {
    setValue(newValue)
  }

  const onBlur = () => {
    if(noSuggestions) {
      setAllowCustomValue(true)
      setNoSuggestions(false)
    }
  }
  // eslint-disable-next-line
  const onSuggestionsFetchRequested = ({ value }) => {
    typingStart()
    loadAutoComplete(value)
  }

  const onSuggestionsClearRequested = () => {
    closeSearch()
  }

  const onSuggestionSelected = (e, { suggestion }) => {
    e.preventDefault()
    onSuggestionSelect(suggestion)
    closeSearch()
    if(resetInputOnSelect) setValue('')
  }

  const onKeyDown = (e) => {
    if(e.keyCode === 13) {
      setAllowCustomValue(true)
      setNoSuggestions(false)
    }
  }

  const getSectionSuggestions = (section) => section.items

  const getSuggestionValue = (suggestion) => suggestion.title
  // eslint-disable-next-line
  const formatResult = (result) => {
    const newResult = []
    for(const title in result) { // eslint-disable-line
      newResult.push({ title, items: result[title] })
    }
    return newResult
  }

  const storeInputReference = (autosuggest) => {
    if(autosuggest !== null) {
      inputRef.current = autosuggest.input
      if(autoFocus) {
        setTimeout(() => {
          inputRef.current.focus()
        }, 100)
      }
    }
  }

  const renderSectionTitle = (section) => <_Divider>{section.title}</_Divider>

  const renderSuggestion = (suggestion, { query, isHighlighted }) => (
    <SearchResultItem
      key={suggestion.id}
      item={suggestion}
      query={query}
      isHighlighted={isHighlighted}
    />
  )

  const suggestions = result ? formatResult(result) : []

  const inputProps = {
    placeholder: placeHolder,
    value,
    onChange,
    onBlur,
    onKeyDown,
    autoFocus,
  }

  const fromHome = source === 'home'

  return (
    <_Container>
      <Autosuggest
        multiSection={multiSection}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        getSectionSuggestions={getSectionSuggestions}
        onSuggestionSelected={onSuggestionSelected}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        inputProps={inputProps}
        ref={storeInputReference}
      />
      {isFetching && (
        <_SpinnerContainer fromHome={fromHome}>
          <Spinner />
        </_SpinnerContainer>
      )}
      {noSuggestions && <AutoCompleteNoResult />}
    </_Container>
  )
}


AutoComplete.propTypes = {
  siteSearchIsOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
  resetInputOnSelect: PropTypes.bool,
  result: PropTypes.shape({
    length: PropTypes.number,
  }),
  onSuggestionSelect: PropTypes.func,
  multiSection: PropTypes.bool,
  isFetching: PropTypes.bool.isRequired,
  placeHolder: PropTypes.string.isRequired,
  loadAutoComplete: PropTypes.func.isRequired,
  typingStart: PropTypes.func.isRequired,
  closeSearch: PropTypes.func.isRequired,
  source: PropTypes.string,
  autoFocus: PropTypes.bool,
}


export default AutoComplete
